/**
 * HubVoiturette.jsx — Hub /permis-am/voiturette
 *
 * Landing dédiée à la formation AM · option quadricycle léger à moteur.
 * Présente les 5 séquences officielles + un aperçu pédagogique Code 2 Roues.
 *
 * Distinction visuelle forte :
 *   - Encadré bleu « Référentiel officiel » (arrêté 08/11/2012)
 *   - Encadré rouge « Enrichi Code 2 Roues »
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Home, Clock, Users, ExternalLink,
  Car, MessagesSquare, BookOpen, Route, ShieldAlert, Bike, GraduationCap, ClipboardList, QrCode,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useSeo from '../../../hooks/useSeo';
import { FORMATION_META, SEQUENCES } from '../../../data/cours/permisAmVoiturette/referentiel';
import { RefOfficiel, RefEnrichi, RefPair, RefList } from '../../../components/RefBlock';
import { ProjectorToggle } from '../../../components/ProjectorToggle';
import LivretQrShareModal from '../../../components/livrets/LivretQrShareModal';
import ProgrammeSlides from '../../../components/voiturette/ProgrammeSlides';
import { useApp } from '../../../contexts/AppContext';

const ICON_MAP = {
  MessagesSquare, Car, BookOpen, Route, ShieldAlert,
};

export default function HubVoiturette() {
  const [qrOpen, setQrOpen] = useState(false);
  const { user } = useApp();
  // Le programme visuel (4 slides pédagogiques) reste réservé aux enseignants
  // et admins authentifiés : c'est le support de cours du prof, pas un contenu
  // que l'élève doit voir avant sa formation.
  const isTeacher = Boolean(user) && ['enseignant', 'admin'].includes(user?.role);

  useSeo({
    title: `${FORMATION_META.title} — Code 2 Roues`,
    description: `Formation ${FORMATION_META.totalDuree} sur 2 jours. Référentiel officiel + pédagogie enrichie Code 2 Roues.`,
  });

  return (
    <div className="min-h-screen bg-background text-foreground pb-16" data-testid="hub-voiturette">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="outline" size="sm" data-testid="btn-back-am">
            <Link to="/permis-am" aria-label="Retour au Hub Permis AM">
              <ArrowLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Permis AM</span>
            </Link>
          </Button>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-red-500 truncate whitespace-nowrap">
              Option quadricycle léger
            </div>
          </div>
          <ProjectorToggle testid="proj-hub-voiturette" />
          <Button
            size="sm"
            onClick={() => setQrOpen(true)}
            className="bg-rose-500 hover:bg-rose-400 text-white font-black uppercase tracking-wider shadow-lg"
            data-testid="btn-qr-hub-voiturette"
            title="Partager cette formation par QR code"
          >
            <QrCode className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">QR élève</span>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/" aria-label="Retour à l'accueil">
              <Home className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">Accueil</span>
            </Link>
          </Button>
        </div>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Hero */}
        <header className="text-center mb-10 lg:mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 mb-4">
            <Car className="w-3 h-3" /> Permis AM · Voiturette / VSP
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {FORMATION_META.title}
          </h1>
          <p className="text-lg lg:text-xl text-red-500 font-black tracking-wide">{FORMATION_META.subtitle}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-[11px] font-black text-white/85">
              <Clock className="w-3.5 h-3.5" /> {FORMATION_META.totalDuree}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-[11px] font-black text-white/85">
              <Users className="w-3.5 h-3.5" /> Dès {FORMATION_META.ageMini}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 text-[11px] font-black text-white/85">
              <GraduationCap className="w-3.5 h-3.5" /> {FORMATION_META.repartition}
            </span>
          </div>

          {/* Raccourcis PROF/ÉLÈVE — accès direct QR + Livret interactif */}
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            <Button
              onClick={() => setQrOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white font-black uppercase tracking-wider shadow-xl"
              data-testid="hero-btn-qr-share"
            >
              <QrCode className="w-4 h-4 mr-2" />
              Partager par QR à un élève
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/70 font-black uppercase tracking-wider"
              data-testid="hero-btn-livret-parcours"
            >
              <Link to="/livrets/voiturette-8h">
                <BookOpen className="w-4 h-4 mr-2" />
                Livret interactif 8h (Élève & Enseignant)
              </Link>
            </Button>
          </div>
        </header>

        {/* Programme visuel — 4 slides pédagogiques conformes référentiel
            Réservé aux enseignants/admins authentifiés (support de cours prof) */}
        {isTeacher ? (
          <ProgrammeSlides />
        ) : (
          <section
            className="mt-8 mb-10 mx-auto max-w-3xl rounded-2xl border-2 border-rose-500/30 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent p-5 sm:p-6 print:hidden"
            data-testid="programme-slides-locked"
            aria-live="polite"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-shrink-0 mx-auto sm:mx-0 w-14 h-14 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-rose-300" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-[9.5px] font-black uppercase tracking-[0.22em] text-rose-300 mb-1.5">
                  Support enseignant · Réservé
                </div>
                <h3 className="text-lg sm:text-xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Programme visuel 4 sessions · réservé aux moniteurs
                </h3>
                <p className="mt-1 text-[13px] text-white/70 leading-snug">
                  Les 4 slides pédagogiques officielles (Sessions 1 à 4) sont un support
                  de cours. Elles sont accessibles aux <strong className="text-white">enseignants Code 2 Roues</strong> connectés.
                  Ton moniteur pourra te les projeter en séance ou te les partager par QR code.
                </p>
              </div>
              <div className="flex flex-col gap-1.5 sm:flex-shrink-0">
                <Button
                  asChild
                  size="sm"
                  className="bg-rose-500 hover:bg-rose-400 text-white font-black uppercase tracking-wider"
                  data-testid="programme-locked-login"
                >
                  <Link to={`/login?next=${encodeURIComponent('/permis-am/voiturette')}`}>
                    Se connecter
                  </Link>
                </Button>
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-white/20 text-white/80 hover:bg-white/5"
                  data-testid="programme-locked-teacher"
                >
                  <Link to={`/register?next=${encodeURIComponent('/permis-am/voiturette')}&role=enseignant`}>
                    Compte moniteur
                  </Link>
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Repere réglementaire général */}
        <div className="mb-10">
          <RefPair>
            <RefOfficiel
              title="Cadre légal de la formation"
              subtitle="Ce que la loi impose à toute auto-école"
              duration={FORMATION_META.totalDuree}
              testid="hub-officiel-cadre"
            >
              <RefList items={[
                `${FORMATION_META.totalDuree} de formation pratique minimum`,
                `${FORMATION_META.repartition}`,
                `Conduite effective : ${FORMATION_META.conduiteEffective}`,
                '5 séquences obligatoires dans l’ordre',
                'Adaptation à l’option quadricycle léger à moteur',
              ]} />
              <p className="text-[11.5px] text-sky-100/70 italic pt-2 border-t border-sky-500/25">
                Source : {FORMATION_META.sourceOfficielle}
              </p>
            </RefOfficiel>

            <RefEnrichi
              title="Notre approche pédagogique"
              subtitle="Ce que Code 2 Roues ajoute par-dessus"
              testid="hub-enrichi-approche"
            >
              <RefList
                tone="red"
                items={[
                  'Chaque séquence enrichie de QCM, scénarios et check-lists',
                  "Distinction visuelle claire : Officiel ↔ Enrichi",
                  "Contenu 100% adapté à la voiturette (pas au cyclo)",
                  "Résumé auto-école généré en fin de séance (Markdown à coller dans Ediser/Packweb)",
                  "Mode élève mobile-friendly + mode enseignant projection",
                ]}
              />
            </RefEnrichi>
          </RefPair>
        </div>

        {/* CTA Livret Parcours pédagogique complet (le plus riche) */}
        <Link
          to="/livrets/parcours-am"
          className="group mb-4 block p-5 rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-600/20 via-rose-500/10 to-transparent hover:border-amber-500/70 transition"
          data-testid="cta-livret-parcours"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-0.5">⭐ Livret pédagogique maître</div>
                <div className="text-xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Parcours AM Topolino — 36 fiches complètes
                </div>
                <p className="text-white/60 text-[13px] mt-0.5">5 séquences officielles · objectif + comprendre + pourquoi + risques + influences + QCM + réflexe · validation Acquis/À renforcer.</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-amber-400 group-hover:text-amber-300">
              Ouvrir le parcours <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </Link>

        {/* CTA Livret 12 Thèmes AM + Livret suivi Topolino */}
        <div className="mb-8 grid gap-3 md:grid-cols-2">
          <Link
            to="/permis-am/themes"
            className="group block p-5 rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-600/15 via-red-500/5 to-transparent hover:border-red-500/60 transition"
            data-testid="cta-themes-am"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-0.5">Livret de révision</div>
                <div className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Livret Thèmes du Permis AM
                </div>
                <p className="text-white/60 text-[13px] mt-0.5">Signalisation · priorités · distances · virages · partage · angles morts · météo · entretien · risques.</p>
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-red-400 group-hover:text-red-300">
                  Ouvrir le livret <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/livrets/topolino-am"
            className="group block p-5 rounded-2xl border-2 border-rose-500/30 bg-gradient-to-br from-rose-600/15 via-rose-500/5 to-transparent hover:border-rose-500/60 transition"
            data-testid="cta-livret-topolino"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0">
                <ClipboardList className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-400 mb-0.5">Livret pédagogique interactif</div>
                <div className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Livret de suivi Topolino
                </div>
                <p className="text-white/60 text-[13px] mt-0.5">Mercredi maîtrise · samedi circulation · 18 étapes à cocher · sync élève/enseignant · impression.</p>
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-rose-400 group-hover:text-rose-300">
                  Ouvrir le livret <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Grille des 5 séquences */}
        <section className="mb-10">
          <h2 className="text-2xl sm:text-3xl font-black mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Les 5 séquences officielles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SEQUENCES.map((s) => {
              const Icon = ICON_MAP[s.icon] || Route;
              return (
                <Link
                  key={s.id}
                  to={`/permis-am/voiturette/${s.slug}`}
                  className="group rounded-2xl border-2 border-white/10 bg-[#141416] hover:border-red-500/50 transition overflow-hidden flex flex-col"
                  data-testid={`hub-voiturette-card-${s.slug}`}
                >
                  {s.poster && (
                    <div className="relative aspect-[3/2] overflow-hidden bg-white flex items-center justify-center">
                      <img
                        src={s.poster}
                        alt={s.posterAlt || s.shortTitle}
                        className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${s.color}25`, color: s.color }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/50">
                      {s.duree}
                    </div>
                  </div>
                  <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: s.color }}>
                    {s.kicker}
                  </div>
                  <h3 className="mt-1 text-lg font-black leading-tight text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {s.shortTitle}
                  </h3>
                  <p className="mt-2 text-[13px] text-white/65 leading-relaxed flex-1">
                    {s.subtitle}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-red-400 group-hover:text-red-300">
                    Ouvrir la séquence <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* CTA vers la version cyclo */}
        <section className="mt-12 p-6 rounded-2xl border-2 border-white/10 bg-black/20">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-1">Autre option AM</div>
              <h3 className="text-lg font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Tu formes plutôt un élève en cyclomoteur ?
              </h3>
              <p className="text-white/60 text-[13px] mt-1">Retrouve la version cyclomoteur (scooter/mob 50 cm³) sur son propre parcours dédié.</p>
            </div>
            <Button asChild className="bg-red-600 hover:bg-red-500 text-white">
              <Link to="/permis-am"><Bike className="w-4 h-4 mr-1.5" /> Voir l’option Cyclomoteur</Link>
            </Button>
          </div>
        </section>

        <p className="mt-8 text-[11px] text-white/40 text-center">
          <a
            href={FORMATION_META.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-white/60 underline underline-offset-4"
          >
            Consulter le référentiel officiel sur Légifrance <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </article>

      {/* Modale QR — partage du hub Voiturette + du livret sur smartphone élève */}
      <LivretQrShareModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        livretPath="/permis-am/voiturette"
        livretTitle="Permis AM · Quadricycle léger"
        livretSubtitle="Voiturette / VSP · 5 séquences officielles"
        accentColor="#f43f5e"
        tabletMode
        bypassAuth
      />
    </div>
  );
}
